import { useEffect, useState } from "react";
import { axiosInstance } from "../../utils/api";

interface Feedback {
    _id: string;
    content: string;
    user: {
        _id: string;
        email: string;
    }; // it's just user id
    createdAt: string;
    updatedAt: string;
}

export const Feedbacks = () => {
    const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(10);
    const [loading, setLoading] = useState<boolean>(false);

    const formatDate = (dateString: string) => {
        const d = new Date(dateString);
        const yy = String(d.getUTCFullYear()).slice(2);
        const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(d.getUTCDate()).padStart(2, '0');
        const hh = String(d.getUTCHours()).padStart(2, '0');
        const min = String(d.getUTCMinutes()).padStart(2, '0');
        return `${yy}-${mm}-${dd} ${hh}:${min}`;
    };

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const { data } = await axiosInstance.get('/feedbacks', {
                    params: { offset, limit }
                });
                const nextFeedbacks = data.feedbacks ?? [];
                const nextTotal = data.total ?? nextFeedbacks.length;
                const nextOffset = data.offset ?? offset;
                const nextLimit = data.limit ?? limit;
                setFeedbacks(nextFeedbacks);
                setTotal(nextTotal);
                setOffset(nextOffset);
                setLimit(nextLimit);
            } finally {
                setLoading(false);
            }
        })();
    }, [offset, limit]);

    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
    const currentPage = Math.min(totalPages, Math.floor(offset / Math.max(1, limit)) + 1);
    const startItem = total === 0 ? 0 : offset + 1;
    const endItem = Math.min(offset + limit, total);

    const goToPage = (page: number) => {
        const clamped = Math.min(Math.max(1, page), totalPages);
        setOffset((clamped - 1) * limit);
    };
    const goPrev = () => goToPage(currentPage - 1);
    const goNext = () => goToPage(currentPage + 1);
    const changePageSize: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
        const newLimit = Number(e.target.value);
        setLimit(newLimit);
        setOffset(0);
    };

    if (!loading && feedbacks.length === 0) {
        return (
            <div className="container">
                <div className="d-flex justify-content-between align-items-center mt-3">
                    <h3 className="m-0">Feedbacks</h3>
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small">Page size:</span>
                        <select className="form-select form-select-sm w-auto" value={limit} onChange={changePageSize}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>
                <div className="text-center text-muted py-5">No feedbacks</div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="d-flex justify-content-between align-items-center mt-3">
                <h3 className="m-0">Feedbacks</h3>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Page size:</span>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={changePageSize}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-striped table-hover align-middle mt-3">
                <thead>
                    <tr>
                        <th>Content</th>
                        <th>User</th>
                        <th>Created At</th>
                        <th>Updated At</th>
                    </tr>
                </thead>
                <tbody>
                    {feedbacks.map((feedback) => (
                        <tr key={feedback._id}>
                            <td>{feedback.content}</td>
                            <td>{feedback.user ? feedback.user.email : 'N/A'}</td>
                            <td>{formatDate(feedback.createdAt)}</td>
                            <td>{formatDate(feedback.updatedAt)}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                <div className="text-muted small">{`Showing ${startItem}-${endItem} of ${total}`}</div>
                <nav>
                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={goPrev} disabled={currentPage === 1}>Previous</button>
                        </li>
                        {Array.from({ length: totalPages }).slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2)).map((_, idx, arr) => {
                            const start = Math.max(1, currentPage - 2);
                            const pageNum = start + idx;
                            return (
                                <li key={pageNum} className={`page-item ${pageNum === currentPage ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => goToPage(pageNum)}>{pageNum}</button>
                                </li>
                            );
                        })}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={goNext} disabled={currentPage >= totalPages}>Next</button>
                        </li>
                    </ul>
                </nav>
            </div>
            {loading && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ background: 'rgba(255,255,255,0.6)' }}>
                    <div className="spinner-border text-secondary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
        </div>
    );
};